import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps';

// Existing active cities + small upcoming blips
const CITY_MARKERS = [
  {
    id: 'mumbai',
    name: 'Mumbai',
    coords: [72.8777, 19.076],
    status: 'ACTIVE',
    textOffset: [-16, 0],
    locations: [
      { type: 'STORE', label: 'Bandra', slug: '/bandra' },
    ],
  },
  {
    id: 'hyderabad',
    name: 'Hyderabad',
    coords: [78.4867, 17.385],
    status: 'ACTIVE',
    textOffset: [-4, 0],
    // splitAboveCount: how many of `locations` to render ABOVE the city blip
    // (the rest go below). Helps avoid overlap with other markers.
    splitAboveCount: 4,
    locations: [
      // Above-blip group (top → bottom, listed bottom-most first)
      { type: 'STORE', label: 'Narsingi',     slug: '/narsingi' },
      { type: 'STORE', label: 'Kompally',     slug: '/kompally' },
      { type: 'STORE', label: 'KPHB',         slug: '/kphb' },
      { type: 'STORE', label: 'LB Nagar',     slug: '/lb-nagar' },
      // Below-blip group (top → bottom)
      { type: 'POD',   label: 'Banjara Hills',slug: '/banjara-hills' },
      { type: 'POD',   label: 'Kondapur',     slug: '/kondapur' },
      { type: 'VAN',   label: 'Van',          slug: '/hyderabad-van-1' },
    ],
  },
  {
    id: 'rajahmundry',
    name: 'Rajahmundry',
    coords: [81.804, 17.0],
    status: 'ACTIVE',
    textOffset: [14, 0],
    locations: [
      { type: 'STORE', label: 'Rajahmundry', slug: '/rajahmundry' },
    ],
  },
];

// Tiny upcoming-city blips — hover/tap reveals the name only.
// `colorIdx` rotates through Petfolk brand colors, `delay` gives each blip
// an independent twinkle phase so they pulse in randomized rhythm.
const PETFOLK_BLIP_COLORS = ['#FF8C42', '#8B5CF6', '#0EA5E9', '#FF6B9D'];
function blipFor(idx) {
  return PETFOLK_BLIP_COLORS[idx % PETFOLK_BLIP_COLORS.length];
}
// Pseudo-random but stable delays per blip (deterministic across renders)
function blipDelay(idx) {
  const seq = [0, 1.7, 0.9, 2.3, 0.4, 1.2, 2.6, 0.6, 1.9, 0.2, 2.1];
  return seq[idx % seq.length];
}
function blipDuration(idx) {
  const seq = [2.4, 3.1, 2.7, 3.4, 2.9, 3.2, 2.6, 3.0, 2.8, 3.3, 2.5];
  return seq[idx % seq.length];
}

const UPCOMING_CITIES = [
  { id: 'delhi',      name: 'Delhi',         coords: [77.2090, 28.6139] },
  { id: 'noida',      name: 'Noida',         coords: [77.3910, 28.5355] },
  { id: 'jaipur',     name: 'Jaipur',        coords: [75.7873, 26.9124] },
  { id: 'kolkata',    name: 'Kolkata',       coords: [88.3639, 22.5726] },
  { id: 'ahmedabad',  name: 'Ahmedabad',     coords: [72.5714, 23.0225] },
  { id: 'pune',       name: 'Pune',          coords: [73.8567, 18.5204] },
  { id: 'vijayawada', name: 'Vijayawada',    coords: [80.6480, 16.5062] },
  { id: 'vizag',      name: 'Vizag',         coords: [83.2185, 17.6868] },
  { id: 'bengaluru',  name: 'Bengaluru',     coords: [77.5946, 12.9716] },
  { id: 'chennai',    name: 'Chennai',       coords: [80.2707, 13.0827] },
  { id: 'kochi',      name: 'Kochi',         coords: [76.2673, 9.9312]  },
];

const TYPE_COLOR  = { STORE: '#FF8C42', VAN: '#0EA5E9', POD: '#8B5CF6' };
const TYPE_SYMBOL = { STORE: '■', VAN: '●', POD: '▲' };
const TYPE_LABEL  = { STORE: 'Store', VAN: 'Van', POD: 'Pod' };
const ROW_H = 15;

function tStyle(sz, wt, color) {
  return {
    fontFamily: 'Outfit, sans-serif',
    fontSize: sz,
    fontWeight: wt,
    fill: color,
    userSelect: 'none',
  };
}

export default function IndiaMap() {
  const navigate = useNavigate();
  const [geoData,     setGeoData]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [hoveredCity, setHoveredCity] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isDark,      setIsDark]      = useState(false);

  useEffect(() => {
    const checkDark = () => setIsDark(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Use merged outline (no state borders) — includes POK + Aksai Chin
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/india-outline`)
      .then(r => r.json())
      .then(data => { setGeoData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="relative w-full select-none" style={{ background: 'transparent' }}>
      <style>{`
        @keyframes markerPulse {
          0%   { transform: scale(1);   opacity: 0.55; }
          75%  { transform: scale(2.8); opacity: 0;    }
          100% { transform: scale(2.8); opacity: 0;    }
        }
        .pulse-ring { animation: markerPulse 2s ease-out infinite; transform-box: fill-box; transform-origin: center; }

        /* Upcoming-blip twinkle — color stays Petfolk-branded, opacity + scale pulse */
        @keyframes blipTwinkle {
          0%, 100% { opacity: 0.45; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.4); }
        }
        .blip-core { animation: blipTwinkle 2.6s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }

        /* Outer ring — radial wave so it's visibly "alive" but tiny */
        @keyframes blipRing {
          0%   { transform: scale(0.6); opacity: 0.7; }
          80%  { transform: scale(2.6); opacity: 0;   }
          100% { transform: scale(2.6); opacity: 0;   }
        }
        .blip-ring { animation: blipRing 2.6s ease-out infinite; transform-box: fill-box; transform-origin: center; }
      `}</style>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 1, pointerEvents: 'none' }}>
          <div style={{
            width: 200, height: 240,
            border: '2px solid #FF8C42',
            borderRadius: '38% 32% 36% 40%',
            opacity: 0.2,
            animation: 'pulse 2s infinite',
          }} />
        </div>
      )}

      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ center: [82, 22], scale: 1080 }}
        width={800}
        height={700}
        style={{ width: '100%', height: 'auto', display: 'block', background: 'transparent' }}
      >
        {geoData && (
          <Geographies geography={geoData}>
            {({ geographies }) =>
              geographies.map(geo => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={{
                    default: { fill: 'none', stroke: '#FF8C42', strokeWidth: 1.0, outline: 'none' },
                    hover:   { fill: 'rgba(255,140,66,0.06)', stroke: '#FF8C42', strokeWidth: 1.2, outline: 'none' },
                    pressed: { fill: 'none', outline: 'none' },
                  }}
                />
              ))
            }
          </Geographies>
        )}

        {CITY_MARKERS.filter(c => c.status !== 'HIDDEN').map(city => {
          const isActive  = city.status === 'ACTIVE';
          const isHovered = hoveredCity === city.id;
          const [dx, dy]  = city.textOffset || [0, 0];

          // City name: light in dark mode, dark in light mode
          const activeCityColor   = isDark ? '#F3F4F6' : '#1C1C1C';
          const upcomingCityColor = isDark ? '#9CA3AF' : '#6B7280';

          // Split locations into above/below groups
          const splitN = city.splitAboveCount ?? 0;
          const aboveLocs = splitN > 0 ? city.locations.slice(0, splitN) : [];
          const belowLocs = splitN > 0 ? city.locations.slice(splitN) : city.locations;

          return (
            <Marker key={city.id} coordinates={city.coords}>
              <g
                onMouseEnter={() => setHoveredCity(city.id)}
                onMouseLeave={() => setHoveredCity(null)}
              >
                {/* Constant pulse ring for active cities */}
                {isActive && (
                  <circle
                    className="pulse-ring"
                    cx="0" cy="0"
                    r={9}
                    fill="none"
                    stroke="#FF8C42"
                    strokeWidth={2}
                  />
                )}

                {/* Core blip — always at (0,0) */}
                <circle
                  r={isActive ? 6 : 4}
                  fill="white"
                  stroke={isActive ? '#FF8C42' : '#9CA3AF'}
                  strokeWidth={isActive ? 2.5 : 1.5}
                />

                {/* City name — placement depends on whether we have ABOVE locations */}
                {(isActive || isHovered) && (
                  <text
                    textAnchor="middle"
                    x={dx}
                    y={(aboveLocs.length > 0 ? -14 - aboveLocs.length * ROW_H : -14) + dy}
                    style={tStyle(12, 700, isActive ? activeCityColor : upcomingCityColor)}
                  >
                    {city.name}
                  </text>
                )}

                {/* ABOVE-blip locations (rendered top → bottom; nearest blip last) */}
                {isActive && aboveLocs.map((loc, idx) => {
                  const itemKey   = `${city.id}-a-${idx}`;
                  const isHovItem = hoveredItem === itemKey;
                  const color     = isHovItem ? '#FF6B1A' : TYPE_COLOR[loc.type];
                  // top item furthest from blip; row idx 0 = closest to blip
                  const rowY = -8 - (aboveLocs.length - 1 - idx) * ROW_H + dy;
                  return (
                    <g
                      key={itemKey}
                      onMouseEnter={() => loc.slug && setHoveredItem(itemKey)}
                      onMouseLeave={() => setHoveredItem(null)}
                      onClick={e => { e.stopPropagation(); if (loc.slug) navigate(loc.slug); }}
                      style={{ cursor: loc.slug ? 'pointer' : 'default' }}
                    >
                      <text
                        textAnchor="middle"
                        x={dx}
                        y={rowY}
                        style={tStyle(10, 600, color)}
                      >
                        {TYPE_SYMBOL[loc.type]}{loc.label ? ` ${loc.label}` : ''}
                      </text>
                    </g>
                  );
                })}

                {/* BELOW-blip locations (rendered top → bottom) */}
                {isActive && belowLocs.map((loc, idx) => {
                  const itemKey   = `${city.id}-b-${idx}`;
                  const isHovItem = hoveredItem === itemKey;
                  const color     = isHovItem ? '#FF6B1A' : TYPE_COLOR[loc.type];
                  const rowY      = 15 + idx * ROW_H + dy;
                  return (
                    <g
                      key={itemKey}
                      onMouseEnter={() => loc.slug && setHoveredItem(itemKey)}
                      onMouseLeave={() => setHoveredItem(null)}
                      onClick={e => { e.stopPropagation(); if (loc.slug) navigate(loc.slug); }}
                      style={{ cursor: loc.slug ? 'pointer' : 'default' }}
                    >
                      <text
                        textAnchor="middle"
                        x={dx}
                        y={rowY}
                        style={tStyle(10, 600, color)}
                      >
                        {TYPE_SYMBOL[loc.type]}{loc.label ? ` ${loc.label}` : ''}
                      </text>
                    </g>
                  );
                })}
              </g>
            </Marker>
          );
        })}

        {/* Upcoming-city blips — tiny, Petfolk-coloured, randomized twinkle. Hover/tap reveals name. */}
        {UPCOMING_CITIES.map((uc, idx) => {
          const isHovered = hoveredCity === uc.id;
          const color = blipFor(idx);
          const delay = blipDelay(idx);
          const dur   = blipDuration(idx);
          const labelColor = isDark ? '#E5E7EB' : '#374151';
          return (
            <Marker key={uc.id} coordinates={uc.coords}>
              <g
                onMouseEnter={() => setHoveredCity(uc.id)}
                onMouseLeave={() => setHoveredCity(null)}
                onTouchStart={() => setHoveredCity(uc.id)}
                style={{ cursor: 'pointer' }}
                data-testid={`upcoming-city-${uc.id}`}
              >
                {/* Larger invisible hit area for easy hover/tap */}
                <circle r={11} fill="transparent" />

                {/* Animated outer ring — independently delayed per blip */}
                <circle
                  className="blip-ring"
                  r={3}
                  fill="none"
                  stroke={color}
                  strokeWidth={1.2}
                  style={{ animationDelay: `${delay}s`, animationDuration: `${dur}s` }}
                />

                {/* Twinkling core dot */}
                <circle
                  className={isHovered ? '' : 'blip-core'}
                  r={isHovered ? 4 : 2.6}
                  fill={color}
                  style={{
                    animationDelay: `${delay}s`,
                    animationDuration: `${dur}s`,
                    transition: 'r 0.15s',
                    filter: 'drop-shadow(0 0 1.5px rgba(255,255,255,0.6))',
                  }}
                />

                {isHovered && (
                  <g>
                    <rect
                      x={-uc.name.length * 3 - 6}
                      y={-22}
                      width={uc.name.length * 6 + 12}
                      height={15}
                      rx={4}
                      fill={isDark ? 'rgba(30,30,40,0.92)' : 'rgba(255,255,255,0.95)'}
                      stroke={color}
                      strokeWidth={0.8}
                    />
                    <text
                      textAnchor="middle"
                      x={0}
                      y={-12}
                      style={tStyle(9, 600, labelColor)}
                    >
                      {uc.name}
                    </text>
                  </g>
                )}
              </g>
            </Marker>
          );
        })}
      </ComposableMap>

      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1.5 mt-3 px-4">
        {Object.entries(TYPE_SYMBOL).map(([type, sym]) => (
          <div key={type} className="flex items-center gap-2 text-sm">
            <span style={{ color: TYPE_COLOR[type], fontWeight: 700, fontSize: 13 }}>{sym}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{TYPE_LABEL[type]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
